// ============================================================================
// src/pig/SowsView.tsx  —  Phase 2 Round 6
// ----------------------------------------------------------------------------
// Breeding pigs tab — sow/boar registry + archived records.
//
// ITS SEVEN READS AND ALL THREE OF ITS WRITES ARE ITS OWN. `pig.sows.core`
// owns the reads; the three app_store writes that used to be App props
// threaded through PigEntry — persistBreeders, persistBreedOptions,
// persistOriginOptions — are this file's now, through
// src/lib/appStoreBlobApi.ts, which holds App's retry/8s-race/never-throws/
// setSaveStatus semantics verbatim so the operator-visible failure posture is
// the same code and not a copy of it.
//
// ONE MIRROR STAYED, AND THE RULE IS "DOES ANYTHING READ IT". `setBreeders` is
// PigContext's raw setter, not an App prop, so it is neither a read nor a boot
// dependency — and App's `triggerMaintenance` snapshot default
// (`breeders: next.breeders || breeders`) is a live reader of the value it
// keeps current. Dropping it would let a later broiler-batches / layer-groups /
// webforms save republish the webform_config `active_groups` list from a
// pre-edit registry, because `computeSubCurrentCount` counts transfers out of
// exactly this array. `setBreedOptions` and `setOriginOptions` had NO reader
// anywhere in src/ and went with their writes.
//
// Breeder form state still comes from usePig(): UI state the provider happens
// to host, seeded from module-scope literals rather than from any boot read.
// ============================================================================
import React from 'react';
import {
  useAppDraftGuard,
  useAppGuardedNavigation,
  useAppLocation as useLocation,
  useAppNavigate as useNavigate,
} from '../app/navigation/index.js';
import {createBreederDraftAutosave, type DraftSaveState} from './breederDraftAutosave.js';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {sb} from '../lib/supabase.js';
import {fmtDate, fmtMDY, todayISO, addDays} from '../lib/dateUtils.js';
import {recordSeqNavOptions, labeledSeqItems} from '../lib/recordSequence.js';
import {S} from '../lib/styles.js';
import {
  calcBreedingTimeline,
  buildCycleSeqMap,
  cycleLabel,
  resolveFarrowingSire,
  DEFAULT_BOAR_NAMES,
  DEFAULT_BREED_OPTIONS,
  DEFAULT_ORIGIN_OPTIONS,
  PIG_GROUPS,
} from '../lib/pig.js';
import {expectedFrom, newPigRequestId, pigRejectionMessage} from '../lib/pigOperationsApi.js';
import {createRecordOpRunner, sameRecordValue} from './pigRecordOps.js';
import PigConflictNotice, {type PigConflictState} from './PigConflictNotice.js';
// The `no-unused-vars` disables these five imports used to carry are deleted,
// not retargeted: the core rule is off for TypeScript files and
// @typescript-eslint/no-unused-vars resolves JSX references, so the directives
// themselves reported as unused.
import InlineNotice from '../shared/InlineNotice.jsx';
import DataTable from '../shared/DataTable.jsx';
import Collapsible from '../shared/Collapsible.js';
import Button from '../shared/Button.js';
import Modal, {ModalFooter} from '../shared/Modal.js';
import StatusText from '../shared/StatusText.jsx';
import Badge from '../shared/Badge.jsx';
import WeightHistoryChart from '../shared/WeightHistoryChart.js';
import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {RecordPageBody, RecordBackLink, RecordPageNotFound, RecordTitle} from '../shared/RecordPageShell.jsx';
import type {PigBreederRow, PigBreedingCycle, PigBreedingTimeline, PigTransferSource} from '../lib/pig.js';
import type {FarrowingCountsRecord} from '../lib/pigFarrowBatch.js';
import type {RecordSeqEntry} from '../lib/recordSequence.js';
import type {Notice} from '../shared/InlineNotice.js';
import {addPigBreedOptionOnServer, addPigOriginOptionOnServer} from '../lib/appStoreConfigOperationApi.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {canReadImportedBreederNotes, type AuthState as AccessAuthState} from '../lib/access.js';
import {useConfirm} from '../contexts/ConfirmContext.jsx';
import {usePig} from '../contexts/PigContext.jsx';
import {useQueryClient} from '@tanstack/react-query';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {PigSowsCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import {sowsMissingMultipleCycles} from './farrowingMisses.js';
import './breedingRedesign.css';

const BREEDING_PIG_ENTITY_TYPE = 'pig.breeder';

const BREEDER_ACTIVITY_LABELS = {
  tag: 'Tag',
  sex: 'Sex',
  group: 'Group',
  status: 'Status',
  breed: 'Breed',
  origin: 'Origin',
  birthDate: 'Birth date',
  lastWeight: 'Last recorded weight',
  purchaseDate: 'Purchase date',
  purchaseAmount: 'Purchase amount',
  notes: 'Notes',
};

// ── No props ────────────────────────────────────────────────────────────────
// src/pig/PigEntry.tsx mounts this view with none. The four it used to pass
// were App's three `persist*` writers and `resolveSire`.
//
// THE TWO OPTION WRITES ARE STILL NOT DEAD CODE. `ppp-breed-options-v1` and
// `ppp-origin-options-v1` do not exist in the production app_store, which makes
// them look removable. They are written the first time an operator adds a
// custom breed or origin — right here — and
// tests/static/dead_hydration_removed_static.test.js pins their boot hydration
// for exactly that reason. The keys did not move; only the writer did.
//
// `resolveSire` was an App closure over App's OWN breedingCycles and boarNames,
// so it read two former-eager boot values through a channel the provenance
// derivation cannot see (it scores hook destructures and prop NAMES; a closure
// is neither). This file has called resolveFarrowingSire directly against
// pig.sows.core's values since that retirement landed, and App no longer builds
// the closure — so the retained-but-unused hand-off this file used to flag is
// gone rather than merely unread.
//
// The two option-list writes now go through app_store_config_operation (migration 249).
// Breeding pigs themselves are written through pig_apply_operation (migration 235).

// ── Row shapes ──────────────────────────────────────────────────────────────

// A breeding pig as this registry reads and writes it. src/lib/pig.ts's
// PigBreederRow names sex / archived / transferredFromBatch and leaves the rest
// to its index signature; everything below is this view's own column set —
// exactly the keys BREEDER_ACTIVITY_LABELS documents, plus the generated id and
// the weigh-in list latestBreederWeight reads.
type BreedingPig = PigBreederRow & {
  // Required, not optional: every render path keys a row by it
  // (`rowProps={(pig) => ({'data-breeding-pig-row': pig.id})}`), the record
  // route encodes it, and the collaboration mount passes it as the entity id —
  // all unguarded, and all pinned byte-for-byte by the raw-source guards.
  id: string;
  // The date stamp PigBatchesView writes alongside the batch/sub-batch names
  // when a feeder pig is saved into breeding. src/lib/pig.ts's
  // PigTransferSource names the two names but not this stamp.
  transferredFromBatch?: (PigTransferSource & {transferDate?: string | null}) | null;
  tag?: string;
  group?: string | null;
  status?: string | null;
  breed?: string | null;
  origin?: string | null;
  birthDate?: string | null;
  lastWeight?: number | string | null;
  purchaseDate?: string | null;
  purchaseAmount?: number | string | null;
  notes?: string | null;
  // A weigh-in entry as the record page renders it: the weight and the date it
  // was taken.
  weighins?: {weight?: number | string | null; date?: string | null}[] | null;
};

// The breeder form. PigContext seeds it from a literal whose eleven fields are
// all strings, and this view copies a stored pig's values straight in, so the
// two numeric-ish columns carry whatever the row held.
type BreederForm = {
  tag: string;
  sex: string;
  group: string;
  status: string;
  breed: string;
  origin: string;
  birthDate: string;
  lastWeight: number | string;
  purchaseDate: string;
  purchaseAmount: number | string;
  notes: string;
};

// The record's editable cards (R25). Every field the spec drives (Tag #,
// Last Recorded Weight, Breed) is on Details, so no flow crosses cards.
type BreederEditorCard = 'details' | 'purchase';
const BREEDER_CARDS: ReadonlyArray<{card: BreederEditorCard; title: string}> = Object.freeze([
  {card: 'details', title: 'Details'},
  {card: 'purchase', title: 'Purchase'},
]);

// Read-first label-left rows (C12) from the stored record; an empty value is
// the muted dash (R14). Last Recorded Weight is edited on Details but read on
// the strip below, which already shows it (R12: no duplicate visual).
function breederDetailRows(pig: BreedingPig, card: BreederEditorCard): [string, string][] {
  const v = (x: unknown) => (x == null || x === '' ? '—' : String(x));
  if (card === 'purchase')
    return [
      ['Purchase Date', pig.purchaseDate ? fmtMDY(pig.purchaseDate) : '—'],
      ['Purchase Amount', pig.purchaseAmount != null && pig.purchaseAmount !== '' ? '$' + pig.purchaseAmount : '—'],
    ];
  return [
    ['Tag #', pig.tag ? '#' + pig.tag : '—'],
    ['Sex', v(pig.sex)],
    ['Group', pig.group ? 'Group ' + pig.group : '—'],
    ['Status', v(pig.status)],
    ['Breed', v(pig.breed)],
    ['Origin', v(pig.origin)],
    ['Birth Date', pig.birthDate ? fmtMDY(pig.birthDate) : '—'],
  ];
}

// R08: a record card is radius 18 on --shadow-card; a card title is 16/800.
const recordCardS: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-card)',
};
const cardTitleS: React.CSSProperties = {fontSize: 16, fontWeight: 800, color: 'var(--ink)'};

// The originating list order, handed through route state by
// recordSeqNavOptions. Absent on a direct link, which is what hides the
// prev/next controls.

function editableBreederFields(form: BreederForm) {
  const {tag, sex, group, status, breed, origin, birthDate, lastWeight, purchaseDate, purchaseAmount} = form;
  return {tag, sex, group, status, breed, origin, birthDate, lastWeight, purchaseDate, purchaseAmount};
}

type SowsLocation = {pathname: string; state?: {recordSeq?: RecordSeqEntry[] | null} | null};

// CustomSelect's onChange takes React's real select event AND the synthetic
// `{target: {value}}` its own add-new path hands it. Both are existing call
// sites, so the parameter admits both rather than the synthetic call being
// rewritten.
type CustomSelectChange = React.ChangeEvent<HTMLSelectElement> | {target: {value: string}};

// One export/print column: a header and a cell reader over a breeding pig.

// The DataTable column shape as this view builds it. src/shared/DataTable.tsx
// declares DataTableColumn but does not export it, so the fields this file
// actually uses are named here rather than that module being changed.
type BreedingPigTableColumn = {
  key: string;
  label: React.ReactNode;
  align?: 'left' | 'right';
  primary?: boolean;
  mobilePriority?: boolean;
  render?: (row: BreedingPig) => React.ReactNode;
};

// One boar in the sire leaderboard, accumulated across farrowing records.
type BoarLeaderboardEntry = {name: string; litters: number; born: number; dead: number; alive: number};

// One row of a sow's farrowing history: the cycle she was exposed in (null for
// a record that matched no cycle), that cycle's timeline, the farrowing record
// if one landed in the window, and the sire the record resolves to.
type SowFarrowHistoryEntry = {
  cycle: PigBreedingCycle | null;
  tl: PigBreedingTimeline | null;
  rec: FarrowingCountsRecord | undefined;
  sire: string | null;
  missed: boolean;
};

// The pig_dailys columns the SOWS/BOARS feed roll-up reads. DailysRecentContext
// holds the rows as `unknown[]`; App loads them with `select('*')` and nothing
// in TypeScript describes the table.
type SowsDailyRow = {date?: string | null; batch_label?: string | null; feed_lbs?: number | string | null};

// The not-in-record-mode value of recordRawId. `null` at runtime, exactly as
// before; typed as the string arm because recordRawId is only READ inside
// `if (recordMode)`, where the ternary took that arm — and the decodeURIComponent
// call there is pinned byte-for-byte by a raw-source guard, so the assertion
// lives on the declaration rather than on the call.
const NO_RECORD_ID = null as unknown as string;

const PIG_SOWS_PARAMS = Object.freeze({});
// Migration 221: list_pig_breeder_imported_notes admits admin, management and
// farm_team only. Any other role that opens this page (Equipment Tech, with pig
// program access) asks pig.sows.core NOT to read the imported notes, rather
// than having the whole page fail on the refused read.
const PIG_SOWS_PARAMS_WITHOUT_IMPORTED_NOTES = Object.freeze({importedNotes: false});

function BreederLeaderboard({
  title,
  rows,
  expanded,
  onToggle,
}: {
  title: string;
  rows: {id: string; name: string; meta: string; alive: number; open?: () => void}[];
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <section className="breeder-board">
      <div className="breeder-board-head">
        <span className="breeder-trophy" aria-hidden="true">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
          </svg>
        </span>
        <div>
          <h2>{title}</h2>
          <p>Ranked by most piglets alive</p>
        </div>
        {rows.length > 5 && (
          <Button type="button" onClick={onToggle}>
            {expanded ? 'Show Top 5' : `Show All ${rows.length}`}
          </Button>
        )}
      </div>
      <div className="breeder-board-rows">
        {rows.length === 0 && <p className="breeder-muted">No litters recorded yet.</p>}
        {(expanded ? rows : rows.slice(0, 5)).map((row, i) => {
          const content = (
            <>
              <span className="breeder-rank">{i + 1}</span>
              <span className="breeder-board-name">
                <strong>{row.name}</strong>
                <small>{row.meta}</small>
              </span>
              <span className="breeder-board-score">
                <strong>{row.alive}</strong> alive
              </span>
            </>
          );
          return row.open ? (
            <button type="button" className="breeder-board-row wcf-clickable" key={row.id} onClick={row.open}>
              {content}
            </button>
          ) : (
            <div className="breeder-board-row" key={row.id}>
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// A26: the recorded list stays the source of truth; malformed historical
// values remain visible there but cannot create invalid chart coordinates.
// The chart itself is the shared §4 trend line (src/shared/WeightHistoryChart,
// the same one the cattle and sheep records use); a chart needs 2+ weights.
function BreederWeightHistoryChart({weighins}: {weighins: NonNullable<BreedingPig['weighins']>}) {
  const points = weighins
    .map((entry, index) => ({
      key: String(index),
      date: String(entry.date || '').slice(0, 10),
      time: Date.parse(entry.date || ''),
      weight: Number(entry.weight),
      raw: entry.weight,
    }))
    .filter(
      (point) => point.raw != null && point.raw !== '' && Number.isFinite(point.time) && Number.isFinite(point.weight),
    )
    .sort((a, b) => a.time - b.time);
  if (points.length < 2) return null;
  const number = (value: number) => value.toLocaleString(undefined, {maximumFractionDigits: 1});
  return (
    <WeightHistoryChart
      label="Recorded weight over time"
      fmtDate={(date) => fmtDate(date)}
      points={points.map((point, index) => {
        const change = index ? point.weight - points[index - 1].weight : null;
        return {
          key: point.key,
          date: point.date,
          weight: point.weight,
          detail:
            change == null
              ? 'First recorded weight'
              : `${change >= 0 ? '+' : ''}${number(change)} lb since previous weight`,
        };
      })}
    />
  );
}

// CustomSelect component for breed/origin with add-new
function CustomSelect({
  value,
  onChange,
  options,
  onAdd,
  placeholder,
}: {
  value: string;
  onChange: (event: CustomSelectChange) => void;
  options: string[];
  onAdd: (value: string) => void;
  placeholder?: string;
}) {
  const [adding, setAdding] = React.useState(false);
  const [newVal, setNewVal] = React.useState('');
  return (
    <div>
      {!adding ? (
        <div style={{display: 'flex', gap: 6}}>
          <select aria-label={placeholder || 'Select option'} value={value} onChange={onChange} style={{flex: 1}}>
            <option value="">{placeholder || 'Select...'}</option>
            {/* A stored value outside the option list (an imported or since-
                removed breed/origin) stays visible and selected; without its
                own option the select showed the placeholder over real data. */}
            {value && !options.includes(value) && <option value={value}>{value}</option>}
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <Button type="button" onClick={() => setAdding(true)}>
            + Add
          </Button>
        </div>
      ) : (
        <div style={{display: 'flex', gap: 6}}>
          <input
            autoFocus
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            placeholder="New option..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onAdd(newVal);
                onChange({target: {value: newVal}});
                setAdding(false);
                setNewVal('');
              }
            }}
          />
          <Button
            type="button"
            onClick={() => {
              onAdd(newVal);
              onChange({target: {value: newVal}});
              setAdding(false);
              setNewVal('');
            }}
          >
            Save
          </Button>
          <Button
            type="button"
            onClick={() => {
              setAdding(false);
              setNewVal('');
            }}
          >
            ✕
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SowsView() {
  const {confirmDelete} = useConfirm();
  const location = useLocation() as SowsLocation;
  const navigate = useNavigate();
  const requestGuardedNavigation = useAppGuardedNavigation();
  // `setSaveStatus` drives the header's Saving / Saved / Error indicator. It was
  // App's `sbSave` that drove it; the extracted writer takes the sink so the
  // signal is identical.
  const {authState, setSaveStatus} = useAuth();
  const [leaderboardExpanded, setLeaderboardExpanded] = React.useState(false);
  const [boarLeaderboardExpanded, setBoarLeaderboardExpanded] = React.useState(false);
  const breedersRef = React.useRef<BreedingPig[]>([]);
  const editorActive = React.useRef(true);
  const deletingBreeder = React.useRef(false);
  const [isDeletingBreeder, setIsDeletingBreeder] = React.useState(false);
  // R25 (owner amendment 2026-10-24: autosave with Done): the record reads
  // first and one card at a time opens the existing editor in place. Done and a
  // card switch both await the same flush; this one hold covers both (and
  // blocks edits, Delete and navigation while held), so a Done that clears the
  // editor can never race a switch that opens a card over it.
  const [editingCard, setEditingCard] = React.useState<BreederEditorCard | null>(null);
  const breederTransition = React.useRef(false);
  const [breederBusy, setBreederBusy] = React.useState(false);
  const [showArchived, setShowArchived] = usePersistentViewState('pig.sows.archivedOpen', false);
  const [notice, setNotice] = React.useState<Notice | null>(null);
  // ONLY THE UI STATE AND ONE MIRROR SETTER COME FROM usePig NOW. The seven
  // former-eager values that used to be destructured or hooked here —
  // breedingCycles, farrowingRecs, boarNames, breeders, breedOptions,
  // originOptions and useDailysRecent().pigDailys — are read through
  // pig.sows.core below. `setBreeders` stays: a setter is not a read, and this
  // one has a live consumer — App's `triggerMaintenance` snapshot default,
  // which is what republishes the webform_config `active_groups` list.
  // `setBreedOptions` and `setOriginOptions` do NOT stay: nothing in src/ reads
  // either value off usePig(), so they were mirror writes with no reader.
  const {
    setBreeders: setBreedersRaw,
    breederForm: breederFormRaw,
    setBreederForm: setBreederFormRaw,
    showBreederForm,
    setShowBreederForm,
    editBreederId: editBreederIdRaw,
    setEditBreederId,
    sowSearch,
    setSowSearch,
  } = usePig();
  const sowsResource = useRouteResource(
    RESOURCE.PIG_SOWS_CORE,
    canReadImportedBreederNotes(authState as AccessAuthState)
      ? PIG_SOWS_PARAMS
      : PIG_SOWS_PARAMS_WITHOUT_IMPORTED_NOTES,
  );
  const sowsResourceRef = React.useRef(sowsResource);
  sowsResourceRef.current = sowsResource;
  const sowsData = sowsResource.data as PigSowsCoreData | null;
  // Breeders are published only after confirmed persistence. The editable
  // draft remains separate, so failed writes cannot become the activity or
  // retry baseline. Custom option lists retain their existing optimistic UI.
  const [savedBreeders, setSavedBreeders] = React.useState<BreedingPig[] | null>(null);
  const [savedBreedOptions, setSavedBreedOptions] = React.useState<string[] | null>(null);
  const [savedOriginOptions, setSavedOriginOptions] = React.useState<string[] | null>(null);
  // The one place this view states what it believes the opaque arrays hold. The
  // shapes are src/lib/pig.ts's plus this view's own columns.
  const breedingCycles = (sowsData?.breedingCycles || []) as PigBreedingCycle[];
  const farrowingRecs = (sowsData?.farrowingRecs || []) as FarrowingCountsRecord[];
  const breeders = savedBreeders ?? ((sowsData?.breeders || []) as BreedingPig[]);
  breedersRef.current = breeders;
  const boarNames = sowsData?.boarNames ?? DEFAULT_BOAR_NAMES;
  // Never empty and never undefined: pig.sows.core falls back to the same seed
  // lists PigContext used, because App's boot hydrates both option keys
  // conditionally.
  const breedOptions = savedBreedOptions ?? sowsData?.breedOptions ?? [...DEFAULT_BREED_OPTIONS];
  const originOptions = savedOriginOptions ?? sowsData?.originOptions ?? [...DEFAULT_ORIGIN_OPTIONS];
  const setBreeders = setBreedersRaw as unknown as React.Dispatch<React.SetStateAction<BreedingPig[]>>;
  const breederForm = breederFormRaw as BreederForm;
  const setBreederForm = setBreederFormRaw as unknown as React.Dispatch<React.SetStateAction<BreederForm>>;
  // The open record's id. PigContext types it `unknown`; this view only ever
  // sets it to a pig's id or null.
  const editBreederId = editBreederIdRaw as string | null;
  const pigDailys = (sowsData?.pigDailys || []) as SowsDailyRow[];
  const queryClient = useQueryClient();
  const queryClientRef = React.useRef(queryClient);
  queryClientRef.current = queryClient;
  // Every breeding-pig write is one 235 operation (see persistBreeder); the
  // runner adopts the server's row and re-reads on a replay or conflict.
  const [applyBreederOp] = React.useState(() =>
    createRecordOpRunner<BreedingPig>({
      sb,
      rowKey: 'breeder',
      deletedKey: 'breeder_id',
      getRows: () => breedersRef.current,
      setRows: (next) => {
        breedersRef.current = next;
        setSavedBreeders(next);
        setBreeders(next);
        // The adopted row lives in THIS mount's state. The cached pig.*.core
        // reads still held the pre-save document, and the record and list
        // routes reseed from them on remount, so a record reopened right after
        // a confirmed save showed (and then edited from) the old values. A
        // refetch alone races that remount, so this view's own cache takes the
        // confirmed rows at once; the other breeder readers are marked stale.
        queryClientRef.current.setQueriesData<PigSowsCoreData>({queryKey: [RESOURCE.PIG_SOWS_CORE]}, (old) =>
          old ? {...old, breeders: next} : old,
        );
        for (const id of [RESOURCE.PIG_SOWS_CORE, RESOURCE.PIG_HOME_CORE, RESOURCE.PIG_BREEDING_CORE]) {
          void queryClientRef.current.invalidateQueries({queryKey: [id]});
        }
      },
      reread: () => void sowsResourceRef.current?.refetch(),
      onSaveStatus: setSaveStatus,
    }),
  );
  // The sire label, computed here instead of through the App closure the
  // `resolveSire` prop carried. Same helper, same two inputs, same
  // null-when-no-cycle-matches answer — the difference is only WHOSE
  // breedingCycles and boarNames it reads, and that difference is the whole
  // point of the retirement. See the props type above.
  //
  // `?? null` keeps the `string | null` contract the prop declared. The helper's
  // return type widens to include `undefined` only because its own boarNames
  // parameter is optional-valued; this resource always supplies both names, so
  // the coalesce cannot fire at runtime and no rendered value changes.
  const resolveSire = (record: unknown): string | null =>
    resolveFarrowingSire(record as FarrowingCountsRecord, breedingCycles, boarNames) ?? null;
  const cycleSeqMap = buildCycleSeqMap(breedingCycles);
  const multiMiss = sowsMissingMultipleCycles(breedingCycles, farrowingRecs, cycleSeqMap);
  // ── Helpers ──
  function pigAge(birthDate: string | null | undefined) {
    if (!birthDate) return '—';
    const b = new Date(birthDate + 'T12:00:00');
    const t = new Date();
    // `dateA - dateB` coerces through Date.prototype.valueOf, which IS
    // getTime(); naming it produces the same number.
    const days = Math.round((t.getTime() - b.getTime()) / 86400000);
    if (days < 0) return '—';
    const y = Math.floor(days / 365),
      rem = days % 365,
      m = Math.floor(rem / 30),
      d = rem % 30;
    if (y > 0) return `${y}y ${m}m`;
    if (m > 0) return `${m}m ${d}d`;
    return `${d}d`;
  }

  function sowFarrowStats(tag: string | null | undefined) {
    const recs = farrowingRecs.filter((r) => (r.sow as string).trim() === String(tag).trim());
    const born = recs.reduce((s, r) => s + (parseInt(r.totalBorn as string) || 0), 0);
    const dead = recs.reduce((s, r) => s + (parseInt(r.deaths as string) || 0), 0);
    return {litters: recs.length, alive: born - dead, born};
  }

  // resolveSire is defined above (shared between farrowing + sows views)

  // Sort: group first (blank last), then tag numerically
  function sortPigs(list: BreedingPig[]) {
    return [...list].sort((a, b) => {
      const ga = a.group || '99',
        gb = b.group || '99';
      if (ga !== gb) return ga.localeCompare(gb, undefined, {numeric: true});
      return (parseFloat(a.tag as string) || 0) - (parseFloat(b.tag as string) || 0);
    });
  }

  function retainedBreederNotes(pig: BreedingPig) {
    return [
      ...new Set(
        [pig.notes, ...(sowsData?.importedNotesByBreederId?.[pig.id] || [])].filter(
          (note): note is string => typeof note === 'string' && note.trim() !== '',
        ),
      ),
    ].join('\n\n');
  }

  const breederSearchQ = (sowSearch || '').trim().toLowerCase();
  function breederMatchesSearch(pig: BreedingPig) {
    if (!breederSearchQ) return true;
    return [
      pig.tag,
      pig.sex,
      pig.group ? 'Group ' + pig.group : '',
      pig.status,
      pig.breed,
      pig.origin,
      retainedBreederNotes(pig),
    ]
      .map((v) => String(v || '').toLowerCase())
      .some((v) => v.includes(breederSearchQ));
  }

  const activePigs = sortPigs(breeders.filter((p) => !p.archived && breederMatchesSearch(p)));
  const archivedPigs = sortPigs(breeders.filter((p) => p.archived && breederMatchesSearch(p)));
  const activeSows = activePigs.filter((p) => p.sex === 'Sow' || p.sex === 'Gilt');
  const activeBoars = activePigs.filter((p) => p.sex === 'Boar');
  const breedingPigSeqRows = [...activeSows, ...activeBoars, ...(showArchived ? archivedPigs : [])];
  const filteredBreederCount = breedingPigSeqRows.length;
  const recordMode = location.pathname.startsWith('/pig/sows/');
  const recordRawId = recordMode ? location.pathname.slice('/pig/sows/'.length).split('/')[0] || '' : NO_RECORD_ID;
  let recordId = recordRawId;
  if (recordMode) {
    try {
      recordId = decodeURIComponent(recordRawId);
    } catch {
      recordId = recordRawId;
    }
  }
  const recordSeq = location.state?.recordSeq || null;
  const recordPig = recordMode ? breeders.find((p) => String(p.id) === String(recordId)) : null;

  function goToBreedingPigs() {
    void requestGuardedNavigation(() => {
      setEditBreederId(null);
      setEditingCard(null);
      navigate('/pig/sows');
    });
  }

  // `any`, matching the sibling conversion in src/broiler/PoultryDailyPage.tsx:
  // RecordSequenceNav's onNavigate takes RecordSeqId (= unknown), which cannot be
  // handed to encodeURIComponent, and this call is pinned byte-for-byte by
  // tests/static/record_sequence_nav_cp3_static.test.js.
  function navigateSeq(id: any) {
    void requestGuardedNavigation(() => {
      setEditBreederId(null);
      setEditingCard(null);
      navigate('/pig/sows/' + encodeURIComponent(id), recordSeqNavOptions(recordSeq));
    });
  }

  function openBreedingPigRecord(pig: BreedingPig, rows: BreedingPig[]) {
    navigate('/pig/sows/' + encodeURIComponent(pig.id), recordSeqNavOptions(labeledSeqItems(rows, 'tag')));
  }

  function openBreedingPigEditor(pig: BreedingPig) {
    setNotice(null);
    setBreederForm({
      tag: pig.tag || '',
      sex: pig.sex || 'Sow',
      group: pig.group || '',
      status: pig.status || 'Sow Group',
      breed: pig.breed || '',
      origin: pig.origin || '',
      birthDate: pig.birthDate || '',
      lastWeight: pig.lastWeight ?? '',
      purchaseDate: pig.purchaseDate || '',
      purchaseAmount: pig.purchaseAmount ?? '',
      notes: '',
    });
    setEditBreederId(pig.id);
    setShowBreederForm(false);
  }

  function flushBreederEdit() {
    if (deletingBreeder.current) return Promise.resolve(false);
    return draftAutosave.flush();
  }

  // A card's Edit. With no card open it opens the existing editor with a clean
  // draft (the one the record used to auto-open). With another card open, that
  // card settles FIRST through the same flush Done uses; on success the shared
  // draft carries over, on a failed save the open card stays with its draft.
  async function editBreederCard(card: BreederEditorCard) {
    if (!recordPig || deletingBreeder.current || breederTransition.current || editingCard === card) return;
    if (editingCard === null) {
      // Never discard an unconfirmed draft: a dirty draft for THIS pig reopens
      // as it is (a refetch never reseeds it); one for another pig refuses
      // until it settles. Only a clean editor is seeded from the record.
      if (draftAutosave.isDirty()) {
        if (editBreederId !== recordPig.id) return;
      } else {
        openBreedingPigEditor(recordPig);
      }
      setEditingCard(card);
      return;
    }
    breederTransition.current = true;
    setBreederBusy(true);
    try {
      if (!(await flushBreederEdit())) return;
      setEditingCard(card);
    } finally {
      breederTransition.current = false;
      setBreederBusy(false);
    }
  }

  // Done: the same flush the record's guarded exits use; close only once it
  // confirmed. There is no Revert over values already saved.
  async function doneBreederEdit() {
    if (deletingBreeder.current || breederTransition.current) return;
    breederTransition.current = true;
    setBreederBusy(true);
    try {
      if (!(await flushBreederEdit())) return;
      setEditingCard(null);
      setEditBreederId(null);
    } finally {
      breederTransition.current = false;
      setBreederBusy(false);
    }
  }

  function updateBreederForm(next: BreederForm) {
    if (deletingBreeder.current || breederTransition.current) return;
    setBreederForm(next);
    if (!recordMode || !editBreederId) return;
    draftAutosave.update({form: next, id: editBreederId});
  }

  function breedingPigTitle(pig: BreedingPig | null | undefined) {
    if (!pig) return 'Breeding Pig';
    const sex = pig.sex ? ' ' + pig.sex : '';
    return '#' + (pig.tag || pig.id) + sex;
  }

  function breedingPigEntityLabel(pig: BreedingPig | null | undefined) {
    return pig && pig.tag ? '#' + pig.tag : pig?.id || 'Breeding Pig';
  }

  function latestBreederWeight(pig: BreedingPig) {
    const weighins = Array.isArray(pig.weighins) ? pig.weighins : [];
    return weighins.length > 0 ? weighins[weighins.length - 1].weight : pig.lastWeight;
  }

  function displayCell(value: React.ReactNode): React.ReactNode {
    return value == null || value === '' ? '-' : value;
  }

  // Leaderboard
  const leaderboard = activeSows
    .map((p) => {
      const s = sowFarrowStats(p.tag);
      return {...p, ...s};
    })
    .filter((p) => p.litters > 0)
    .sort((a, b) => b.alive - a.alive);

  const boarStats: Record<string, BoarLeaderboardEntry> = {};
  farrowingRecs.forEach((r) => {
    const sire = (resolveSire(r) || '').trim();
    if (!sire) return;
    const entry = boarStats[sire] ?? {name: sire, litters: 0, born: 0, dead: 0, alive: 0};
    entry.litters++;
    entry.born += parseInt(r.totalBorn as string) || 0;
    entry.dead += parseInt(r.deaths as string) || 0;
    entry.alive = entry.born - entry.dead;
    boarStats[sire] = entry;
  });
  const boarLeaderboard = Object.values(boarStats).sort((a, b) => b.alive - a.alive);

  // Custom options helpers
  // Both writers still CREATE ppp-breed-options-v1 / ppp-origin-options-v1 the
  // first time an operator adds a custom value — that is the whole reason the
  // keys are absent from production — and both still publish locally so the
  // dropdown shows the new option immediately instead of waiting for a remount.
  // What changed is the writer (App's prop became the shared blob API) and the
  // context mirror, which is gone: nothing in src/ reads either option list off
  // usePig(), so keeping it in step kept it in step for nobody.
  function addBreedOption(val: string) {
    const v = val.trim();
    if (!v || breedOptions.includes(v)) return;
    const nb = [...breedOptions, v];
    setSavedBreedOptions(nb);
    void addPigBreedOptionOnServer(sb, {value: v, options: nb}, setSaveStatus);
  }
  function addOriginOption(val: string) {
    const v = val.trim();
    if (!v || originOptions.includes(v)) return;
    const nb = [...originOptions, v];
    setSavedOriginOptions(nb);
    void addPigOriginOptionOnServer(sb, {value: v, options: nb}, setSaveStatus);
  }

  function breederFormError(form: BreederForm, id: string | null): string | null {
    if (!form.tag.trim()) return 'Please enter a tag number.';
    const duplicate = breedersRef.current.find(
      (pig) => (pig.tag as string).trim() === form.tag.trim() && pig.id !== id,
    );
    return duplicate ? `Tag #${form.tag} already exists.` : null;
  }

  // ---- Breeding pigs through pig_apply_operation (235) --------------------
  // A create is breeder.create with an id made once per open form; an edit is
  // ONE breeder.update of the editable fields whose value changed, with the
  // expected-before values read off the persisted row. The server keeps every
  // other stored field (weighins, transferredFromBatch, ...), trims and checks
  // the tag, derives `archived`, and logs the pig.breeder Activity in the same
  // transaction -- so this view emits none.
  const breederCreate = React.useRef<{id: string; intent: string; requestId: string} | null>(null);
  const breederUpdate = React.useRef<{intent: string; requestId: string} | null>(null);
  const [breederConflict, setBreederConflict] = React.useState<PigConflictState | null>(null);
  const breederFormRef = React.useRef(breederForm);
  breederFormRef.current = breederForm;
  async function persistBreeder(form: BreederForm, id: string | null): Promise<BreedingPig | undefined> {
    const breeders = breedersRef.current;
    setNotice(null);
    const error = breederFormError(form, id);
    if (error) {
      setNotice({kind: 'error', message: error});
      return;
    }
    const editable = editableBreederFields(form) as Record<string, unknown>;
    const existing = id ? (breeders.find((b) => b.id === id) as Record<string, unknown> | undefined) : undefined;
    if (id && !existing) {
      setNotice({kind: 'error', message: 'This pig is no longer available. Your edits have not been saved.'});
      return;
    }
    let outcome;
    let recordId: string;
    if (!existing) {
      const intent = JSON.stringify(editable);
      const prior = breederCreate.current;
      recordId = prior?.id || 'bp-' + newPigRequestId().replace(/^pig-/, '');
      const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
      breederCreate.current = {id: recordId, intent, requestId};
      outcome = await applyBreederOp({
        requestId,
        operation: 'breeder.create',
        target: {breeder_id: recordId},
        change: editable,
      });
    } else {
      recordId = id as string;
      const fields = Object.keys(editable).filter((f) => !sameRecordValue(existing[f], editable[f]));
      if (fields.length === 0) return existing as BreedingPig;
      const change = Object.fromEntries(fields.map((f) => [f, editable[f]]));
      const expected = expectedFrom(existing, fields);
      const intent = JSON.stringify([recordId, expected, change]);
      const prior = breederUpdate.current;
      const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
      breederUpdate.current = {intent, requestId};
      outcome = await applyBreederOp({
        requestId,
        operation: 'breeder.update',
        target: {breeder_id: recordId},
        expected,
        change,
      });
    }
    if (outcome.status === 'saved' || outcome.status === 'no_change') {
      if (existing) breederUpdate.current = null;
      else breederCreate.current = null;
      setBreederConflict(null);
      if (!editorActive.current) return;
      const saved = breedersRef.current.find((b) => b.id === recordId);
      return (saved || {...(existing || {}), ...editable, id: recordId}) as BreedingPig;
    }
    if (!editorActive.current) return;
    if (outcome.status === 'conflict') {
      breederUpdate.current = null;
      const conflicts = outcome.conflicts;
      setBreederConflict({
        conflicts,
        labels: BREEDER_ACTIVITY_LABELS,
        useLatest: async () => {
          void sowsResource.refetch();
          setBreederConflict(null);
          const merged = {...breederFormRef.current} as Record<string, unknown>;
          for (const c of conflicts) merged[c.field] = c.current == null ? '' : (c.current as unknown);
          const next = merged as BreederForm;
          setBreederForm(next);
          if (id) draftAutosave.update({form: next, id});
        },
      });
      return;
    }
    const message =
      outcome.status === 'rejected'
        ? pigRejectionMessage(outcome)
        : outcome.status === 'unknown'
          ? outcome.message
          : 'Pig was not saved. Please try again.';
    setNotice({kind: 'error', message: (existing ? 'Not saved: ' : 'Pig was not saved: ') + message});
  }

  const [draftSaveState, setDraftSaveState] = React.useState<DraftSaveState>('idle');
  const draftHandlers = React.useRef({persistBreeder, breederFormError});
  draftHandlers.current = {persistBreeder, breederFormError};
  const [draftAutosave] = React.useState(() =>
    createBreederDraftAutosave<{form: BreederForm; id: string}>({
      validate: ({form, id}) => {
        const error = draftHandlers.current.breederFormError(form, id);
        setNotice(error ? {kind: 'error', message: error} : null);
        return !error;
      },
      persist: async ({form, id}) => Boolean(await draftHandlers.current.persistBreeder(form, id)),
      onState: setDraftSaveState,
    }),
  );
  useAppDraftGuard({
    ownerKey: 'pig.breeder.editor',
    isUnsafeToLeave: () => deletingBreeder.current || breederTransition.current || draftAutosave.isDirty(),
    settleBeforeLeave: () => (breederTransition.current ? Promise.resolve(false) : flushBreederEdit()),
  });
  React.useLayoutEffect(() => {
    editorActive.current = true;
    draftAutosave.resume();
    return () => {
      editorActive.current = false;
      draftAutosave.suspend();
    };
  }, [draftAutosave]);
  React.useEffect(() => {
    // R25 replaces C09's auto-open: the record reads first, and a card's Edit
    // opens the editor. What stays: leaving the record clears an editor nobody
    // is on, a card never stays open over a different pig, and nothing here
    // runs while a draft is unconfirmed or a delete is in flight.
    if (draftAutosave.isDirty() || deletingBreeder.current) return;
    if (!recordMode && editBreederId) setEditBreederId(null);
    if (!recordMode || !recordPig || editBreederId !== recordPig.id) setEditingCard(null);
  }, [draftAutosave, editBreederId, recordMode, recordPig, setEditBreederId]);

  async function saveBreeder() {
    const pig = await persistBreeder(breederForm, null);
    if (!pig) return;
    setShowBreederForm(false);
    setEditBreederId(null);
    openBreedingPigRecord(pig, [...breedersRef.current]);
  }

  function deleteBreeder(pig: BreedingPig) {
    confirmDelete('Delete this pig permanently? This cannot be undone.', async () => {
      if (deletingBreeder.current || breederTransition.current) return;
      deletingBreeder.current = true;
      setIsDeletingBreeder(true);
      try {
        // Finish the last edit before deleting, and freeze the fields while
        // the deletion waits so a later autosave cannot resurrect this row.
        if (!(await draftAutosave.flush())) return;
        const outcome = await applyBreederOp({
          requestId: newPigRequestId(),
          operation: 'breeder.delete',
          target: {breeder_id: pig.id as string},
        });
        if (outcome.status !== 'saved' && outcome.status !== 'no_change') {
          if (editorActive.current) {
            const why = outcome.status === 'rejected' ? pigRejectionMessage(outcome) : 'Please try again.';
            setNotice({kind: 'error', message: 'Pig was not deleted: ' + why});
          }
          return;
        }
      } finally {
        deletingBreeder.current = false;
        if (editorActive.current) setIsDeletingBreeder(false);
      }
      if (!editorActive.current) return;
      setEditBreederId(null);
      navigate('/pig/sows');
    });
  }

  const STATUS_OPTS = ['Sow Group', 'Boar Group', 'Deceased', 'Processed', 'Sold'];
  const SEX_OPTS = ['Sow', 'Gilt', 'Boar'];

  // Build farrowing history for a sow: each cycle she was in, with farrowing record or "missed"
  function sowFarrowHistory(tag: string | null | undefined): SowFarrowHistoryEntry[] {
    var tagStr = String(tag).trim();
    var history: SowFarrowHistoryEntry[] = [];
    breedingCycles.forEach(function (c) {
      var allTags = [...(c.boar1Tags || '').split(/[\n,]+/), ...(c.boar2Tags || '').split(/[\n,]+/)]
        .map(function (t) {
          return t.trim();
        })
        .filter(Boolean);
      if (!allTags.includes(tagStr)) return;
      var tl = calcBreedingTimeline(c.exposureStart);
      if (!tl) return;
      // Find farrowing record for this sow in this cycle's window
      var rec = farrowingRecs.find(function (r) {
        if ((r.sow as string).trim() !== tagStr) return false;
        if (!r.farrowingDate) return false;
        var rd = new Date(r.farrowingDate + 'T12:00:00');
        return (
          rd >= new Date((tl as PigBreedingTimeline).farrowingStart + 'T12:00:00') &&
          rd <= addDays(new Date((tl as PigBreedingTimeline).farrowingEnd + 'T12:00:00'), 14)
        );
      });
      var b1Tags2 = (c.boar1Tags || '')
        .split(/[\n,]+/)
        .map(function (t) {
          return t.trim();
        })
        .filter(Boolean);
      var sire = rec
        ? resolveSire(rec)
        : b1Tags2.includes(tagStr)
          ? c.boar1Name || boarNames.boar1
          : c.boar2Name || boarNames.boar2;
      history.push({cycle: c, tl: tl, rec: rec, sire: sire || null, missed: !rec});
    });
    // Add any farrowing records not matched to a cycle above
    var matchedRecIds = new Set(
      history
        .filter(function (h2) {
          return h2.rec;
        })
        .map(function (h2) {
          return (h2.rec as FarrowingCountsRecord).id;
        }),
    );
    farrowingRecs
      .filter(function (r) {
        return (r.sow as string).trim() === tagStr && !matchedRecIds.has(r.id);
      })
      .forEach(function (r) {
        history.push({cycle: null, tl: null, rec: r, sire: resolveSire(r), missed: false});
      });
    // Sort newest first by farrowing date or cycle exposure date
    history.sort(function (a, b) {
      var da = a.rec ? a.rec.farrowingDate : a.cycle ? a.cycle.exposureStart : '';
      var db = b.rec ? b.rec.farrowingDate : b.cycle ? b.cycle.exposureStart : '';
      // Both sides keep their `|| ''` fallback, unchanged.
      return (db || '').localeCompare(da || '');
    });
    return history;
  }

  // CP3: animal-list rows now render through the shared <DataTable> (real
  // table semantics, openable rows with right-aligned numerics; DataTable owns
  // the keyboard-open wiring). The section chrome (title band + count + muted
  // state) is preserved around it; the view's own grouping/sort/filter still
  // feed `pigs`/`rows` unchanged.
  const breedingPigColumns: BreedingPigTableColumn[] = [
    {
      key: 'tag',
      label: 'Tag',
      primary: true,
      render: (pig) => (
        <span style={{fontWeight: 700, color: 'var(--ink)'}}>{pig.tag ? '#' + pig.tag : '(no tag)'}</span>
      ),
    },
    {key: 'sex', label: 'Sex', render: (pig) => <StatusText tone="muted">{displayCell(pig.sex)}</StatusText>},
    {
      key: 'breed',
      label: 'Breed',
      mobilePriority: false,
      render: (pig) => <StatusText tone="muted">{displayCell(pig.breed)}</StatusText>,
    },
    {
      key: 'origin',
      label: 'Origin',
      mobilePriority: false,
      render: (pig) => <StatusText tone="muted">{displayCell(pig.origin)}</StatusText>,
    },
    {
      key: 'age',
      label: 'Age',
      render: (pig) => <StatusText tone="muted">{pigAge(pig.birthDate)}</StatusText>,
    },
    {
      key: 'weight',
      label: 'Weight',
      align: 'right',
      render: (pig) => {
        const latestWeight = latestBreederWeight(pig);
        return latestWeight ? (
          <span style={{color: 'var(--text-primary)', fontWeight: 700}}>{latestWeight + ' lb'}</span>
        ) : (
          <StatusText tone="muted">{'-'}</StatusText>
        );
      },
    },
    {
      key: 'alive',
      label: 'Alive',
      align: 'right',
      render: (pig) => {
        const isSow = pig.sex === 'Sow' || pig.sex === 'Gilt';
        if (!isSow) return <StatusText tone="muted">{'-'}</StatusText>;
        return <span style={{color: 'var(--text-primary)', fontWeight: 700}}>{sowFarrowStats(pig.tag).alive}</span>;
      },
    },
    {
      key: 'litters',
      label: 'Litters',
      align: 'right',
      render: (pig) => {
        const isSow = pig.sex === 'Sow' || pig.sex === 'Gilt';
        if (!isSow) return <StatusText tone="muted">{'-'}</StatusText>;
        return <span style={{color: 'var(--text-primary)'}}>{sowFarrowStats(pig.tag).litters}</span>;
      },
    },
  ];

  function BreedingPigTableSection({
    title,
    pigs,
    rows = breedingPigSeqRows,
    muted = false,
    band,
  }: {
    title: string;
    pigs: BreedingPig[];
    rows?: BreedingPig[];
    muted?: boolean;
    // Fixed group palette index (Q08); archived groups use the gray token.
    band?: number;
  }) {
    if (!pigs || pigs.length === 0) return null;
    return (
      <div
        className="breeder-group"
        data-breeding-pig-table-section={title}
        data-breeder-group-color={band}
        style={{
          border: '1px solid var(--border)',
          borderRadius: 18,
          overflow: 'hidden',
          background: 'var(--surface)',
        }}
      >
        <Collapsible
          stateKey={'pig-breeders:' + title}
          defaultOpen={!muted}
          open={muted ? showArchived : undefined}
          onOpenChange={muted ? setShowArchived : undefined}
          count={pigs.length}
          label={
            <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
              <span
                aria-hidden="true"
                style={{
                  width: 7,
                  height: 7,
                  flexShrink: 0,
                  borderRadius: '50%',
                  background: muted || !band ? 'var(--group-archived)' : `var(--group-${band})`,
                }}
              />
              <span style={{fontWeight: 700, fontSize: 15, color: 'var(--text-primary)'}}>{title}</span>
            </span>
          }
          summary={
            title !== 'Boars'
              ? `${pigs.reduce((total, pig) => total + sowFarrowStats(pig.tag).alive, 0)} piglets alive`
              : undefined
          }
        >
          <DataTable
            surfaceKey="breeding-pig-table"
            rows={pigs}
            rowKey="id"
            density="comfortable"
            card={{
              main: (pig) => (pig.tag ? '#' + pig.tag : '(no tag)'),
              line1: (pig) =>
                [pig.sex, pigAge(pig.birthDate), latestBreederWeight(pig) ? latestBreederWeight(pig) + ' lb' : null]
                  .filter(Boolean)
                  .join(' · '),
              line2: (pig) =>
                pig.sex === 'Sow' || pig.sex === 'Gilt'
                  ? `${sowFarrowStats(pig.tag).litters} litters · ${sowFarrowStats(pig.tag).alive} alive`
                  : undefined,
            }}
            columns={breedingPigColumns}
            onRowOpen={(pig) => openBreedingPigRecord(pig, rows)}
            rowProps={(pig) => ({'data-breeding-pig-row': pig.id})}
          />
        </Collapsible>
      </div>
    );
  }

  function BreedingPigRecordDetails({pig}: {pig: BreedingPig}) {
    const stats = sowFarrowStats(pig.tag);
    const isSow = pig.sex === 'Sow' || pig.sex === 'Gilt';
    const history = isSow ? sowFarrowHistory(pig.tag) : [];
    const weighins = Array.isArray(pig.weighins) ? pig.weighins : [];
    const latestWeight = latestBreederWeight(pig);

    return (
      <div data-breeding-pig-record-details="1" style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <dl className="breeder-record-metrics">
          {[
            ['Last Recorded Weight', latestWeight ? latestWeight + ' lb' : '—'],
            ['Age', pigAge(pig.birthDate)],
            ...(isSow
              ? [
                  ['Litters', stats.litters],
                  ['Alive Total', stats.alive],
                ]
              : []),
          ].map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        {weighins.length > 0 && (
          <div style={{...recordCardS, padding: 14}}>
            <div style={{...cardTitleS, marginBottom: 8}}>Weight History</div>
            <BreederWeightHistoryChart weighins={weighins} />
            <div data-breeding-pig-weight-history={pig.id} style={{display: 'flex', flexWrap: 'wrap', gap: '6px 12px'}}>
              {weighins
                .slice()
                .reverse()
                .map((w, wi) => (
                  <span key={wi} style={{fontSize: 12, color: 'var(--ink-muted)'}}>
                    <strong style={{color: 'var(--ink)'}}>{w.weight} lb</strong>{' '}
                    <span style={{color: 'var(--ink-faint)'}}>{fmtMDY(w.date)}</span>
                  </span>
                ))}
            </div>
          </div>
        )}

        {isSow && history.length > 0 && (
          <div style={{...recordCardS, padding: 14}}>
            <div style={{...cardTitleS, marginBottom: 8}}>Farrowing History</div>
            <div data-breeding-pig-farrowing-history={pig.id} style={{display: 'flex', flexDirection: 'column'}}>
              {history
                .filter(function (h2) {
                  if (h2.missed && h2.tl && h2.tl.farrowingEnd >= todayISO()) return false;
                  return true;
                })
                .map(function (h, hi) {
                  if (h.missed) {
                    return (
                      <div
                        key={hi}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '7px 0',
                          borderBottom: hi < history.length - 1 ? '1px solid var(--hairline)' : 'none',
                          fontSize: 12,
                        }}
                      >
                        <Badge variant="warn">Missed</Badge>
                        <span style={{color: 'var(--ink-muted)'}}>
                          {cycleLabel(h.cycle, cycleSeqMap) +
                            ' - ' +
                            fmtMDY((h.cycle as PigBreedingCycle).exposureStart)}
                        </span>
                        {h.sire && <span style={{color: 'var(--ink-faint)'}}>{'Sire: ' + h.sire}</span>}
                      </div>
                    );
                  }
                  const born = parseInt((h.rec as FarrowingCountsRecord).totalBorn as string) || 0;
                  const dead = parseInt((h.rec as FarrowingCountsRecord).deaths as string) || 0;
                  const alive = born - dead;
                  return (
                    <div
                      key={hi}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '7px 0',
                        borderBottom: hi < history.length - 1 ? '1px solid var(--hairline)' : 'none',
                        fontSize: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{color: 'var(--ink)', fontWeight: 700, minWidth: 90}}>
                        {fmtMDY((h.rec as FarrowingCountsRecord).farrowingDate)}
                      </span>
                      <span style={{color: 'var(--text-2)'}}>{alive + ' alive'}</span>
                      <span style={{color: 'var(--ink-faint)'}}>{born + ' born'}</span>
                      {dead > 0 && <span style={{color: 'var(--danger-ink)'}}>{dead + ' died'}</span>}
                      {h.cycle && <span style={{color: 'var(--ink-faint)'}}>{cycleLabel(h.cycle, cycleSeqMap)}</span>}
                      {h.sire && <span style={{color: 'var(--ink-faint)'}}>{h.sire}</span>}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {pig.transferredFromBatch &&
          (() => {
            const tfb = pig.transferredFromBatch;
            const sourceLabel = tfb.subBatchName || tfb.batchName || '?';
            const dateStr = tfb.transferDate ? ' on ' + fmtMDY(tfb.transferDate) : '';
            const sexNoun = pig.sex === 'Boar' ? 'boar' : pig.sex === 'Sow' ? 'sow' : 'gilt';
            return (
              <div
                data-breeding-pig-transfer-note={pig.id}
                style={{
                  padding: '8px 12px',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  fontSize: 12,
                  color: 'var(--text-primary)',
                  fontWeight: 700,
                }}
              >
                {'This ' + sexNoun + ' was saved from ' + sourceLabel + dateStr + '.'}
              </div>
            );
          })()}
      </div>
    );
  }

  // `card` limits the fields to one record card (R25); the Add modal passes
  // nothing and keeps every field.
  function renderBreederFields(card?: BreederEditorCard) {
    const show = (k: BreederEditorCard) => !card || card === k;
    return (
      <div className="breeder-fields">
        {show('details') && (
          <>
            <div>
              <label style={S.label}>Tag #</label>
              <input
                aria-label="Tag #"
                value={breederForm.tag}
                onChange={(e) => updateBreederForm({...breederForm, tag: e.target.value})}
                placeholder="e.g. 5"
              />
            </div>
            <div>
              <label style={S.label}>Sex</label>
              <select
                aria-label="Sex"
                value={breederForm.sex}
                onChange={(e) => updateBreederForm({...breederForm, sex: e.target.value})}
              >
                {SEX_OPTS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Group</label>
              <select
                aria-label="Group"
                value={breederForm.group}
                onChange={(e) => updateBreederForm({...breederForm, group: e.target.value})}
              >
                <option value="">No group</option>
                {PIG_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    Group {g}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Status</label>
              <select
                aria-label="Status"
                value={breederForm.status}
                onChange={(e) => updateBreederForm({...breederForm, status: e.target.value})}
              >
                {STATUS_OPTS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>Breed</label>
              <CustomSelect
                value={breederForm.breed}
                onChange={(e) => updateBreederForm({...breederForm, breed: e.target.value})}
                options={breedOptions}
                onAdd={addBreedOption}
                placeholder="Select breed..."
              />
            </div>
            <div>
              <label style={S.label}>Origin</label>
              <CustomSelect
                value={breederForm.origin}
                onChange={(e) => updateBreederForm({...breederForm, origin: e.target.value})}
                options={originOptions}
                onAdd={addOriginOption}
                placeholder="Select origin..."
              />
            </div>
            <div>
              <label style={S.label}>Birth Date</label>
              <input
                aria-label="Birth Date"
                type="date"
                value={breederForm.birthDate}
                onChange={(e) => updateBreederForm({...breederForm, birthDate: e.target.value})}
              />
            </div>
            <div>
              <label style={S.label}>Last Recorded Weight (lbs)</label>
              <input
                aria-label="Last Recorded Weight (lbs)"
                type="number"
                value={breederForm.lastWeight || ''}
                onChange={(e) => updateBreederForm({...breederForm, lastWeight: e.target.value})}
              />
            </div>
          </>
        )}
        {show('purchase') && (
          <>
            <div>
              <label style={S.label}>Purchase Date</label>
              <input
                aria-label="Purchase Date"
                type="date"
                value={breederForm.purchaseDate}
                onChange={(e) => updateBreederForm({...breederForm, purchaseDate: e.target.value})}
              />
            </div>
            <div>
              <label style={S.label}>Purchase Amount ($)</label>
              <input
                aria-label="Purchase Amount ($)"
                type="number"
                value={breederForm.purchaseAmount || ''}
                onChange={(e) => updateBreederForm({...breederForm, purchaseAmount: e.target.value})}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  // FAIL CLOSED, AND BEFORE NOT-FOUND. The registry used to arrive with the boot
  // payload, so a record URL could only ever be resolvable or genuinely absent.
  // A route-local read introduces two more states, and both of them resolve
  // `breeders.find(...)` to undefined — so without these two branches every deep
  // link would render "Breeding pig not found." while the read was still in
  // flight, and again if it failed. That is the RecordPage* fail-closed contract
  // this shell exists for; the ORDER is the whole point.
  //
  // Both branches return a plain <div> wrapper, exactly as the loaded record
  // branch below does, and NOT RecordPageLoading / RecordPageLoadError. That is
  // not decoration: surface_router_derivation classifies a component that
  // returns two or more RecordPage* states as a record PAGE, and this component
  // is a hub with an inline record mode — SURFACE_ROUTERS calls it `sub-mode`
  // and the guard's own comment says so ("SowsView returns RecordPageNotFound
  // for a bad id while remaining a hub"). Reaching for the record shell here
  // would silently reclassify the whole surface.
  if (recordMode && (sowsResource.loading || Boolean(sowsResource.error))) {
    return (
      <div className="breeding-redesign" style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
        <Header />
        <RecordPageBody
          maxWidth={960}
          {...(sowsResource.loading
            ? {'data-breeding-pig-record-loading': 'true'}
            : {'data-breeding-pig-record-load-error': 'true'})}
        >
          <RecordBackLink label="Back to Breeding Pigs" onBack={goToBreedingPigs} />
          {sowsResource.loading ? (
            <div style={{padding: 24, textAlign: 'center', color: 'var(--ink-muted)', fontSize: 14}}>
              Loading breeding pig…
            </div>
          ) : (
            <div style={{padding: 24, textAlign: 'center', color: 'var(--danger-ink)', fontSize: 14}}>
              Could not load the breeding pig registry.{' '}
              <Button type="button" onClick={() => sowsResource.refetch()}>
                Retry
              </Button>
            </div>
          )}
        </RecordPageBody>
      </div>
    );
  }

  if (recordMode && !recordPig) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Breeding Pigs"
        onBack={goToBreedingPigs}
        message="Breeding pig not found."
        data-breeding-pig-record-not-found="true"
      />
    );
  }

  if (recordMode && recordPig) {
    return (
      <div className="breeding-redesign" style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
        <Header />
        <RecordPageBody maxWidth={960} data-breeding-pig-record-loaded="true">
          <RecordBackLink label="Back to Breeding Pigs" onBack={goToBreedingPigs} />
          <RecordSequenceNav seq={recordSeq} currentId={recordId} onNavigate={navigateSeq} layout="inline" />
          <div className="breeder-record-heading">
            <RecordTitle>{breedingPigTitle(recordPig)}</RecordTitle>
            {/* R25: the header's one action while a card is open. */}
            {editingCard !== null && (
              <Button
                variant="primary"
                type="button"
                disabled={isDeletingBreeder || breederBusy}
                onClick={() => void doneBreederEdit()}
                data-breeding-pig-done={recordPig.id}
              >
                Done
              </Button>
            )}
          </div>
          <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
          <PigConflictNotice conflict={breederConflict} />
          {/* R25: read-first cards with label-left rows (C12). Each card's Edit
              opens the existing editor in place; only the open card renders its
              fields, and only it is the "Edit breeding pig" region. */}
          {BREEDER_CARDS.map(({card, title}) => {
            const open = editingCard === card && editBreederId === recordPig.id;
            return (
              <section
                key={card}
                className="breeder-editor"
                data-breeding-pig-card={card}
                aria-label={open ? 'Edit breeding pig' : undefined}
              >
                <div className="breeder-card-head">
                  <div className="breeder-card-title">{title}</div>
                  {!open && (
                    <button
                      type="button"
                      className="wcf-clickable breeder-card-edit"
                      disabled={isDeletingBreeder || breederBusy}
                      data-breeding-pig-edit-card={card}
                      onClick={() => void editBreederCard(card)}
                    >
                      Edit
                    </button>
                  )}
                </div>
                {open ? (
                  <>
                    {draftSaveState === 'failed' && (
                      <div role="alert">
                        Not Saved — Retry. Your edits are still here.
                        <Button type="button" onClick={() => void flushBreederEdit()}>
                          Retry
                        </Button>
                      </div>
                    )}
                    <fieldset disabled={isDeletingBreeder} style={{border: 0, padding: 0, margin: 0, minWidth: 0}}>
                      {renderBreederFields(card)}
                    </fieldset>
                  </>
                ) : (
                  <dl className="breeder-detail-rows">
                    {breederDetailRows(recordPig, card).map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>
            );
          })}
          <BreedingPigRecordDetails pig={recordPig} />
          <RecordCollaborationSection
            sb={sb}
            authState={authState}
            entityType={BREEDING_PIG_ENTITY_TYPE}
            entityId={recordPig.id}
            entityLabel={breedingPigEntityLabel(recordPig)}
            legacyNote={recordPig.notes || undefined}
          />
          {/* R24: Delete is a danger action at the foot, below Comments and
              Activity — the same handler and confirmation it had in the editor. */}
          <Button
            variant="danger"
            type="button"
            className="breeder-delete"
            disabled={isDeletingBreeder || breederBusy}
            onClick={() => deleteBreeder(recordPig)}
          >
            Delete Pig
          </Button>
        </RecordPageBody>
      </div>
    );
  }

  return (
    <div className="breeding-redesign">
      <Header />
      <div className="breeder-list-body">
        <header className="breeder-title">
          <h1>Breeding Pigs</h1>
          <p>
            <strong>{activePigs.length}</strong> pigs · <strong>{activeSows.length}</strong> sows &amp; gilts ·{' '}
            <strong>{activeBoars.length}</strong> boars · {new Set(activeSows.map((p) => p.group).filter(Boolean)).size}{' '}
            groups
          </p>
        </header>
        <div className="breeder-leaderboards">
          <BreederLeaderboard
            title="Sow Leaderboard"
            expanded={leaderboardExpanded}
            onToggle={() => setLeaderboardExpanded((v) => !v)}
            rows={leaderboard.map((p) => ({
              id: p.id,
              name: 'Sow #' + p.tag,
              meta: `${p.litters} ${p.litters === 1 ? 'litter' : 'litters'} · ${p.born} born`,
              alive: p.alive,
              open: () => openBreedingPigRecord(p, breedingPigSeqRows),
            }))}
          />
          <BreederLeaderboard
            title="Boar Leaderboard"
            expanded={boarLeaderboardExpanded}
            onToggle={() => setBoarLeaderboardExpanded((v) => !v)}
            rows={boarLeaderboard.map((b) => ({
              id: b.name,
              name: b.name,
              meta: `${b.litters} ${b.litters === 1 ? 'litter' : 'litters'} · ${b.born} born · ${b.born > 0 ? Math.round((b.alive / b.born) * 100) : 0}% survival`,
              alive: b.alive,
            }))}
          />
        </div>

        {/* Search + add pig */}
        <div
          style={{display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center'}}
        >
          <div style={{display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 280px', maxWidth: 520}}>
            <input
              data-breeding-pig-search="1"
              value={sowSearch || ''}
              onChange={(e) => setSowSearch(e.target.value)}
              placeholder="Search tag, group, breed, status..."
              style={{flex: 1, minWidth: 0}}
            />
            {sowSearch && (
              <Button type="button" data-breeding-pig-search-clear="1" onClick={() => setSowSearch('')}>
                Clear
              </Button>
            )}
          </div>
          <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
            <Button
              variant="primary"
              onClick={() => {
                setNotice(null);
                setBreederForm({
                  tag: '',
                  sex: 'Sow',
                  group: '1',
                  status: 'Sow Group',
                  breed: '',
                  origin: '',
                  birthDate: '',
                  lastWeight: '',
                  purchaseDate: '',
                  purchaseAmount: '',
                  notes: '',
                });
                setEditBreederId(null);
                setShowBreederForm(true);
              }}
            >
              + Add Pig
            </Button>
          </div>
        </div>

        {/* Add/Edit form — modal overlay */}
        {showBreederForm && !editBreederId && (
          <Modal
            title="Add Breeding Pig"
            onClose={() => {
              setNotice(null);
              setShowBreederForm(false);
              setEditBreederId(null);
            }}
            footer={<ModalFooter primaryLabel="Add Pig" onPrimary={saveBreeder} />}
          >
            <div style={{padding: '16px 20px'}}>
              <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
              {renderBreederFields()}
              {breederForm.birthDate && (
                <div style={{marginTop: 8, fontSize: 12, color: 'var(--text-secondary)'}}>
                  Age: {pigAge(breederForm.birthDate)}
                </div>
              )}
              <div style={{display: 'flex', gap: 8, marginTop: 12}}></div>
            </div>
          </Modal>
        )}

        {multiMiss.length > 0 && (
          <section className="breeder-callout" data-breeding-missed-cycles="1">
            <div className="breeder-callout-head">
              <span style={{fontWeight: 700, fontSize: 15, color: 'var(--text-primary)'}}>
                Sows Missing Multiple Cycles
              </span>
              <span className="breeder-count">{multiMiss.length}</span>
              <div className="breeder-group-summary">Failed to farrow in 2 or more cycles — review for culling</div>
            </div>
            <DataTable
              surfaceKey="breeding-missed-cycles"
              rows={multiMiss}
              rowKey="tag"
              card={{
                main: (sow) => '#' + sow.tag,
                line1: (sow) => `${sow.cycles.length} cycles missed`,
                line2: (sow) => (sow.lastFarrow ? 'Last farrowed ' + fmtMDY(sow.lastFarrow) : 'Never farrowed'),
                badge: () => <Badge variant="warn">Missed</Badge>,
              }}
              columns={[
                {key: 'tag', label: 'Sow #', render: (sow) => <strong>#{sow.tag}</strong>},
                {key: 'missed', label: 'Cycles missed', align: 'right', render: (sow) => sow.cycles.length},
                {
                  key: 'lastFarrow',
                  label: 'Last farrowing',
                  render: (sow) => (sow.lastFarrow ? fmtMDY(sow.lastFarrow) : 'Never farrowed'),
                },
                {key: 'cycles', label: 'Missed cycles', render: (sow) => sow.cycles.join(' · ')},
              ]}
            />
          </section>
        )}

        {activeSows.length > 0 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
            {(() => {
              const groups = [...new Set(activeSows.map((p) => p.group || 'none'))].sort((a, b) =>
                a === 'none' ? 1 : b === 'none' ? -1 : a.localeCompare(b, undefined, {numeric: true}),
              );
              return groups.map((grp) => {
                const pigs = activeSows.filter((p) => (p.group || 'none') === grp);
                return (
                  <BreedingPigTableSection
                    key={grp}
                    title={grp !== 'none' ? `Group ${grp}` : 'No Group'}
                    pigs={pigs}
                    rows={breedingPigSeqRows}
                    band={PIG_GROUPS.indexOf(grp) + 1 || undefined}
                  />
                );
              });
            })()}
          </div>
        )}

        <BreedingPigTableSection title="Boars" pigs={activeBoars} rows={breedingPigSeqRows} band={6} />

        <BreedingPigTableSection title="Archived" pigs={archivedPigs} rows={breedingPigSeqRows} muted />

        {/* Three states where there used to be one. The registry arrived with
            the boot payload, so "empty" and "not loaded yet" were the same
            frame and "unreachable" could not happen. OperationalListEmptyState
            answers only the first, so the other two are rendered instead of it
            rather than beside it — "No breeding pigs yet" over a farm with a
            full registry is the empty-farm lie, and it invites a duplicate
            tag. */}
        {sowsResource.loading && (
          <div
            data-pig-sows-loading
            style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
          >
            Loading breeding pigs…
          </div>
        )}
        {!sowsResource.loading && Boolean(sowsResource.error) && (
          <div
            data-pig-sows-load-error
            style={{textAlign: 'center', padding: '3rem', color: 'var(--danger-ink)', fontSize: 13}}
          >
            Could not load breeding pigs.{' '}
            <Button type="button" onClick={() => sowsResource.refetch()}>
              Retry
            </Button>
          </div>
        )}
        {!sowsResource.loading && !sowsResource.error && (
          <OperationalListEmptyState
            totalCount={breeders.length}
            filteredCount={filteredBreederCount}
            emptyLabel="No breeding pigs yet"
            filteredLabel="No breeding pigs match the current search"
            data-empty-state="breeding-pigs"
          />
        )}

        {/* ── FEED CONSUMPTION ── */}
        {(() => {
          // Aggregate feed from pig_dailys for SOWS and BOARS by month
          const sowDailys = pigDailys.filter((d) => d.batch_label?.toUpperCase() === 'SOWS');
          const boarDailys = pigDailys.filter((d) => d.batch_label?.toUpperCase() === 'BOARS');

          function monthlyFeed(dailys: SowsDailyRow[]) {
            const byMonth: Record<string, number> = {};
            dailys.forEach((d) => {
              if (!d.date || !d.feed_lbs) return;
              const ym = d.date.slice(0, 7);
              byMonth[ym] = (byMonth[ym] || 0) + parseFloat(d.feed_lbs as string);
            });
            return Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0]));
          }

          const sowMonths = monthlyFeed(sowDailys);
          const boarMonths = monthlyFeed(boarDailys);
          const sowTotal = sowDailys.reduce((s, d) => s + (parseFloat(d.feed_lbs as string) || 0), 0);
          const boarTotal = boarDailys.reduce((s, d) => s + (parseFloat(d.feed_lbs as string) || 0), 0);

          if (sowMonths.length === 0 && boarMonths.length === 0) return null;

          const fmtMonth = (ym: string) => {
            const [y, m] = ym.split('-');
            return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            });
          };

          return (
            <div
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-card)',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--surface-2)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{fontWeight: 600, fontSize: 14}}>🌾 Feed Consumption — Breeding Stock</div>
              </div>
              <div style={{padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
                {/* SOWS */}
                <div>
                  <div style={{fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8}}>SOWS</div>
                  <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10}}>
                    Total: <strong style={{color: 'var(--ink)'}}>{Math.round(sowTotal).toLocaleString()} lbs</strong>{' '}
                    from {sowDailys.length} reports
                  </div>
                  {sowMonths.slice(0, 12).map(([ym, lbs]) => (
                    <div
                      key={ym}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        borderBottom: '1px solid var(--hairline)',
                        fontSize: 12,
                      }}
                    >
                      <span style={{color: 'var(--ink-muted)'}}>{fmtMonth(ym)}</span>
                      <span style={{fontWeight: 600, color: 'var(--text-primary)'}}>
                        {Math.round(lbs).toLocaleString()} lbs
                      </span>
                    </div>
                  ))}
                </div>
                {/* BOARS */}
                <div>
                  <div style={{fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 8}}>BOARS</div>
                  <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10}}>
                    Total: <strong style={{color: 'var(--ink)'}}>{Math.round(boarTotal).toLocaleString()} lbs</strong>{' '}
                    from {boarDailys.length} reports
                  </div>
                  {boarMonths.slice(0, 12).map(([ym, lbs]) => (
                    <div
                      key={ym}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        borderBottom: '1px solid var(--hairline)',
                        fontSize: 12,
                      }}
                    >
                      <span style={{color: 'var(--ink-muted)'}}>{fmtMonth(ym)}</span>
                      <span style={{fontWeight: 600, color: 'var(--ink)'}}>{Math.round(lbs).toLocaleString()} lbs</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
